/**
 * Zod schemas for the Brand DNA feature.
 *
 * Owned by `prompts/02-features/brand-dna/01-schema-service-api-sonnet-4.6.md`.
 *
 * Two surfaces share these schemas:
 *  - tRPC router input validation (`apps/api/src/routers/brand-dna.ts`)
 *  - AI output validation for `generateObject()` calls in the service layer
 *
 * All schemas are exported individually so callers can compose them.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Sub-schemas
// ---------------------------------------------------------------------------

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

export const colorsSchema = z.object({
  primary: z.string().regex(HEX_COLOR_REGEX, "Must be a 6-digit hex color"),
  secondary: z.string().regex(HEX_COLOR_REGEX),
  accent: z.string().regex(HEX_COLOR_REGEX),
  background: z.string().regex(HEX_COLOR_REGEX),
});
export type Colors = z.infer<typeof colorsSchema>;

export const fontsSchema = z.object({
  heading: z.string().min(1).max(100),
  body: z.string().min(1).max(100),
});
export type Fonts = z.infer<typeof fontsSchema>;

export const toneDescriptorSchema = z.string().min(2).max(50);

export const writingSampleSchema = z
  .string()
  .min(50, "Writing sample must be at least 50 characters")
  .max(5000, "Writing sample must be under 5000 characters");

export const voiceProviderSchema = z.enum(["elevenlabs", "kokoro", "silma"]);
export type VoiceProvider = z.infer<typeof voiceProviderSchema>;

// ---------------------------------------------------------------------------
// Router input schemas
// ---------------------------------------------------------------------------

export const createBrandProfileSchema = z.object({
  workspaceId: z.string().min(1),
  name: z.string().min(1).max(100),
});

export const updateBrandProfileSchema = z.object({
  brandProfileId: z.string().min(1),
  name: z.string().min(1).max(100).optional(),
  logoUrl: z.string().url().optional().nullable(),
  colors: colorsSchema.optional(),
  fonts: fontsSchema.optional(),
  toneDescriptors: z.array(toneDescriptorSchema).max(20).optional(),
  writingSamples: z.array(writingSampleSchema).min(1).max(10).optional(),
  voiceGuide: z.string().max(10_000).optional(),
  voiceProvider: voiceProviderSchema.optional(),
});

export const analyzeToneSchema = z.object({
  brandProfileId: z.string().min(1),
  writingSamples: z
    .array(writingSampleSchema)
    .min(1, "Provide at least 1 writing sample")
    .max(10),
});

export const extractColorsSchema = z.object({
  brandProfileId: z.string().min(1),
  logoUrl: z.string().url("Must be a valid URL"),
});

export const cloneVoiceSchema = z.object({
  brandProfileId: z.string().min(1),
  audioSamples: z
    .array(z.string().url())
    .min(1, "Provide at least 1 audio sample")
    .max(5),
});

export const scoreConsistencySchema = z.object({
  brandProfileId: z.string().min(1),
  content: z.string().min(10).max(50_000),
});

// ---------------------------------------------------------------------------
// AI output schemas (used with `generateObject`)
// ---------------------------------------------------------------------------

export const toneAnalysisOutputSchema = z.object({
  descriptors: z
    .array(
      z.object({
        label: z.string(),
        confidence: z.number().min(0).max(1),
        description: z.string(),
      }),
    )
    .min(3)
    .max(10),
  overallTone: z.string(),
  formality: z.enum([
    "very_formal",
    "formal",
    "neutral",
    "casual",
    "very_casual",
  ]),
  humor: z.enum(["none", "subtle", "moderate", "frequent"]),
  emotionalRange: z.enum([
    "reserved",
    "balanced",
    "expressive",
    "highly_expressive",
  ]),
});
export type ToneAnalysisOutput = z.infer<typeof toneAnalysisOutputSchema>;

export const consistencyScoreOutputSchema = z.object({
  score: z.number().min(0).max(100),
  breakdown: z.object({
    toneMatch: z.number().min(0).max(100),
    vocabularyMatch: z.number().min(0).max(100),
    styleMatch: z.number().min(0).max(100),
    brandAlignmentMatch: z.number().min(0).max(100),
  }),
  suggestions: z.array(z.string()).max(5),
  flaggedPhrases: z
    .array(
      z.object({
        phrase: z.string(),
        issue: z.string(),
        suggestion: z.string(),
      }),
    )
    .max(10),
});
export type ConsistencyScore = z.infer<typeof consistencyScoreOutputSchema>;
